
// "What coverage are you on right now?" — step 2 of the AEP renewal wizard
// (#656, reference screenshots 3-8).
//
// Carrier and plan options come from the plan catalog via oracle
// (/plan_carriers/ and /plan_search/, added in the same issue), scoped to the
// client's county so the Medigap list is a usable dropdown rather than all 308
// national carriers.
//
// Every picker has an "I can't find my carrier" escape to free text. That is
// deliberate and load-bearing: `supp_carrier`/`pdp_carrier`/`mapd_carrier` have
// always been free-text fields that agents read, the catalog only covers plans
// we sell, and a client who cannot find their carrier must not be trapped
// mid-wizard. The escape writes the SAME fields as the dropdown, so nothing
// downstream needs to know which path produced the value.
import CheckCardGroup from '../parts/CheckCardGroup.vue'
import Callout from '../parts/Callout.vue'
// Generated from Dien's medigapstatecopy.csv (#656 comment 1) — 50 states +
// DC keyed by code, plus `_default` (the underwriting copy with no state
// name) for anything the sheet doesn't cover.
import MEDIGAP_STATE_COPY from '~/data/medigap-state-copy.json'
// The same name-cleanup table the agent-facing quoting UI applies via the
// store's parsePlan. Composed differently here (see planLabel): this step also
// strips the carrier the catalog repeats in every plan name, which the quoting
// UI has no need to do because it shows one plan per card, not a dropdown.
import { applyReplacements, PLAN_NAME_REPLACEMENTS, baseCarrierName, groupCarrierVariants, stripCarrierPrefix } from '~/util/plan-name'

const COVERAGE_OPTIONS = [
    {
        value: 'SUPP_PDP',
        label: 'Original Medicare + a Medigap and a drug plan',
        description: 'A Medicare Supplement (Medigap) plus a separate Part D drug plan.',
    },
    {
        value: 'PDP',
        label: 'Original Medicare + a drug plan only',
        description: 'A standalone Part D drug plan, without a Medicare Supplement.',
    },
    {
        value: 'MAPD',
        label: 'A Medicare Advantage plan (all-in-one)',
        description: 'One plan that bundles your coverage, usually with drugs included.',
    },
    {
        value: 'UNSURE',
        label: "I'm not sure",
        description: "That's okay. We'll figure it out together.",
    },
]

// One carrier+plan fieldset per coverage kind, all three sharing the same
// markup and behaviour (#656 Dien feedback round added the plan selects to
// pdp and mapd, which had been carrier-only). `types` is the catalog
// plan_type filter; 'mapd' asks for 'ma' too, since a Medicare Advantage
// plan without drug coverage is still Medicare Advantage and the catalog
// splits those into a separate plan_type.
const PLAN_KINDS = [
    {
        kind: 'supp',
        legend: 'Your current Medicare Supplement (Medigap)',
        carrierPrompt: 'Select your Medigap carrier',
        carrierField: 'supp_carrier',
        planField: 'supp_name',
        idField: 'supp_plan_id',
        types: 'supp',
        planManualPlaceholder: 'e.g. Plan G',
    },
    {
        kind: 'pdp',
        legend: 'Your current Part D drug plan',
        carrierPrompt: 'Select your drug-plan carrier',
        carrierField: 'pdp_carrier',
        planField: 'pdp_name',
        idField: 'pdp_plan_id',
        types: 'pdp',
        planManualPlaceholder: 'Plan name as it appears on your card',
    },
    {
        kind: 'mapd',
        legend: 'Your current Medicare Advantage plan',
        carrierPrompt: 'Select your carrier',
        carrierField: 'mapd_carrier',
        planField: 'mapd_name',
        idField: 'mapd_plan_id',
        types: 'mapd,ma',
        planManualPlaceholder: 'Plan name as it appears on your card',
    },
]
const KIND = Object.fromEntries(PLAN_KINDS.map(k => [k.kind, k]))

const MEDIGAP_REVIEW_OPTIONS = [
    { value: true, label: 'Yes, please review it' },
    { value: false, label: 'No, leave it as it is' },
]

// The "What you can change this fall" bullets, per coverage. SUPP_PDP is
// verbatim from Dien's mockup c1-02; the MAPD and PDP variants follow his
// 2026-09-01 ruling ("a matching education box for their coverage") — copy
// drafted to match the SUPP_PDP tone, flagged for his review. UNSURE gets
// none: the client told us they don't know what they have.
const CHANGE_EDUCATION = {
    SUPP_PDP: [
        { lead: 'Change your Part D drug plan.', rest: "No health questions, and you can't be turned down." },
        { lead: 'Your Medicare Supplement stays exactly as it is', rest: "unless you ask us to look at it. Its benefits are set by law and don't change from year to year." },
    ],
    PDP: [
        { lead: 'Change your Part D drug plan.', rest: "No health questions, and you can't be turned down." },
        { lead: 'Your Original Medicare stays as it is.', rest: 'If you want to look at other kinds of coverage too, your advisor can go over the options with you.' },
    ],
    MAPD: [
        { lead: 'Change your Medicare Advantage plan.', rest: "You can pick a different plan for next year. No health questions, and you can't be turned down." },
        { lead: 'Or move to Original Medicare', rest: 'with a separate drug plan. Your advisor can walk you through what that change would mean.' },
    ],
}

const CARD_HINTS = [
    {
        kind: 'supp',
        badge: 'Medicare Supplement',
        memberId: '0000000000',
        note: 'If your card has a Medicare plan letter, you have a <strong>Medicare Supplement plan</strong>.',
    },
    {
        kind: 'pdp',
        badge: 'Prescription Plan Name (PDP)',
        memberId: 'Hxxxxxxxx',
        note: 'If your card says "PDP" or "Prescriptions" in or next to the plan name, you have a <strong>prescription drug plan</strong>.',
    },
    {
        kind: 'mapd',
        badge: 'Medicare Advantage (HMO)',
        memberId: 'H1234567',
        note: 'If your card says HMO or PPO next to the plan name, or lists copay amounts for office visits, you have a <strong>Medicare Advantage plan</strong>.',
    },
]

export default {
    components: { CheckCardGroup, Callout },
    props: {
        // Accessor proxy from the page (mixins/survey-zip-proxy.js style):
        // the coverage fields write through to the survey form, while
        // fips/state/year are read-only lookup scope.
        target: { type: Object, required: true },
    },
    data () {
        return {
            carriers: { supp: [], pdp: [], mapd: [] },
            plans: { supp: [], pdp: [], mapd: [] },
            loading: { supp: false, pdp: false, mapd: false },
            loadingPlans: { supp: false, pdp: false, mapd: false },
            // Which pickers the client has switched to free text.
            manual: { supp: false, pdp: false, mapd: false },
        }
    },
    computed: {
        coverageOptions: () => COVERAGE_OPTIONS,
        cardHints: () => CARD_HINTS,
        coverage () { return this.target.current_medicare_coverage },
        showSupp () { return this.coverage === 'SUPP_PDP' },
        showPdp () { return this.coverage === 'SUPP_PDP' || this.coverage === 'PDP' },
        showMapd () { return this.coverage === 'MAPD' },
        isUnsure () { return this.coverage === 'UNSURE' },
        visibleKinds () {
            const show = { supp: this.showSupp, pdp: this.showPdp, mapd: this.showMapd }
            return PLAN_KINDS.filter(k => show[k.kind])
        },
        medigapReviewOptions: () => MEDIGAP_REVIEW_OPTIONS,
        changeEducation () { return CHANGE_EDUCATION[this.coverage] || null },
        // Whole years, parsed piecewise: `new Date('YYYY-MM-DD')` reads as UTC
        // midnight, which shifts a birthday by a day in western timezones —
        // enough to flip Minnesota's 65-to-70 gate on the boundary.
        clientAge () {
            const [y, m, d] = (this.target.dob || '').split('-').map(Number)
            if (!y || !m || !d) { return null }
            const now = new Date()
            const hadBirthday = now.getMonth() + 1 > m || (now.getMonth() + 1 === m && now.getDate() >= d)
            return now.getFullYear() - y - (hadBirthday ? 0 : 1)
        },
        medigapStateCopy () {
            const entry = MEDIGAP_STATE_COPY[this.target.state] || MEDIGAP_STATE_COPY._default
            // Minnesota's birthday-rule copy only holds for ages 65 to 70; an
            // out-of-range or unverifiable age gets the underwriting default
            // (per the CSV's condition column).
            if (entry.condition === 'age65to70' && !(this.clientAge >= 65 && this.clientAge <= 70)) {
                return entry.fallback
            }
            return entry
        },
        // The lookup scope. Recomputed rather than captured so a client who
        // corrects their ZIP on an earlier step gets the right catalog.
        scope () {
            return {
                year: this.target.year,
                fips: this.target.fips || undefined,
                state: this.target.state || undefined,
            }
        },
    },
    watch: {
        // Load only the lists the chosen card actually needs — an unfiltered
        // preload of all three would be three catalog queries for a client who
        // is about to pick "I'm not sure".
        coverage: { immediate: true, handler: 'loadForCoverage' },
    },
    methods: {
        patch (deltas) {
            Object.entries(deltas).forEach(([k, v]) => { this.$set(this.target, k, v === '' ? null : v) })
        },
        // #721 round 2: the id of the merged row behind the chosen label (the
        // first discount variant, like `merged` in loadPlans) and the catalog
        // year, beside the label the client signs. A label the list does not
        // know (legacy raw name, nothing loaded) stores the label alone.
        pickPlan (kind, label) {
            const { planField, idField } = KIND[kind]
            const hit = (this.plans[kind] || []).find(p => p.label === label)
            const patch = { [planField]: label, [idField]: hit ? hit.id : null }
            if (hit) { patch.current_plans_year = this.target.year || null }
            this.patch(patch)
        },
        planPlaceholder (kind) {
            if (!this.target[KIND[kind].carrierField]) { return 'Choose a carrier first' }
            return this.loadingPlans[kind] ? 'Loading plans…' : 'Select your plan'
        },
        setCoverage (value) {
            if (value === this.coverage) { return }
            // Clear the fields belonging to the card we just left, so a client
            // who switches from Advantage to Medigap doesn't silently keep a
            // stale mapd_carrier the agent would later read as current.
            this.patch({
                current_medicare_coverage: value,
                supp_carrier: null,
                supp_name: null,
                pdp_carrier: null,
                pdp_name: null,
                mapd_carrier: null,
                mapd_name: null,
                supp_plan_id: null,
                pdp_plan_id: null,
                mapd_plan_id: null,
                current_plans_year: null,
                // A stale "yes" from a coverage the client corrected would
                // wrongly pre-check Medicare Supplement on the SOA later.
                medigap_review_requested: null,
            })
            this.plans = { supp: [], pdp: [], mapd: [] }
            this.manual = { supp: false, pdp: false, mapd: false }
        },
        toggleManual (kind) {
            this.$set(this.manual, kind, !this.manual[kind])
            // Switching modes clears the value: a carrier picked from the
            // catalog and a hand-typed one are different claims, and carrying
            // one into the other's field would misrepresent which it was.
            const { carrierField, planField, idField } = KIND[kind]
            this.patch({ [carrierField]: null, [planField]: null, [idField]: null })
            this.$set(this.plans, kind, [])
        },
        async loadForCoverage () {
            for (const k of this.visibleKinds) {
                await this.loadCarriers(k.kind)
                // Hydrated survey may already carry a carrier; load its plan
                // list so the saved plan name renders on first paint.
                if (this.target[k.carrierField] && !this.plans[k.kind].length) {
                    await this.loadPlans(k.kind)
                }
            }
        },
        async loadCarriers (kind) {
            if (this.carriers[kind].length || this.loading[kind]) { return }
            this.$set(this.loading, kind, true)
            const resp = await this.$api.searchPlanCarriers({ ...this.scope, types: KIND[kind].types }).catch(() => null)
            this.$set(this.carriers, kind, resp?.results || [])
            this.$set(this.loading, kind, false)
        },
        baseCarrierName (name) {
            return baseCarrierName(name)
        },
        // Catalog carriers grouped by display name, first-appearance order.
        carrierOptions (kind) {
            return groupCarrierVariants(this.carriers[kind])
        },
        // Every raw catalog variant behind a display name — falls back to the
        // name itself for values the catalog doesn't know (manual entries,
        // out-of-area saves).
        variantsFor (kind, name) {
            const group = this.carrierOptions(kind).find(g => g.name === name)
            return group ? group.variants : [name]
        },
        async pickCarrier (kind, carrier) {
            // Changing carrier invalidates the plan beneath it (and its id).
            const { carrierField, planField, idField } = KIND[kind]
            this.patch({ [carrierField]: carrier, [planField]: null, [idField]: null })
            this.$set(this.plans, kind, [])
            if (carrier) { await this.loadPlans(kind) }
        },
        // The name to show (and store) for a catalog row: `preferred_name`
        // when an agent has curated one in Salesforce, else the legacy
        // replacement table, else the raw catalog name.
        planLabel (plan) {
            const preferred = (plan?.preferred_name || '').trim()
            // A curated override is authoritative and already written for a
            // human to read. Never strip words out of one.
            if (preferred) { return preferred }
            const raw = (plan?.name || '').trim()
            if (!raw) { return plan?.name || '' }
            // Carrier strip BEFORE the replacement table, because the table
            // rewrites carrier text wherever it appears -- /Harvard Pilgrim
            // Health Care/ -> 'Harvard Pilgrim' fires inside the plan name too,
            // which shortens the carrier there, desyncs it from plan.carrier
            // and strands the tail ("Inc. Medigap Core Plan"). Stripping first
            // also keeps the table off text that is about to be discarded.
            const stripped = this.stripCarrierPrefix(raw, plan?.carrier)
            // A catalog name that was nothing but the carrier keeps its text.
            const base = stripped || raw
            return applyReplacements(base, PLAN_NAME_REPLACEMENTS) || base
        },
        // The catalog name a curated label replaced -- what the plan is "also
        // known as". Null when the label IS the catalog name, so the great
        // majority of plans (which have no override) get no redundant echo.
        planAka (plan, label = null) {
            const shown = label ?? this.planLabel(plan)
            const raw = (plan?.name || '').trim()
            if (!raw || raw === shown) { return null }
            const rest = this.stripCarrierPrefix(raw, plan?.carrier)
            // An empty remainder means the catalog name was nothing but the
            // carrier, which tells the client nothing worth a second line.
            return rest && rest !== shown ? rest : null
        },
        stripCarrierPrefix (name, carrier) {
            return stripCarrierPrefix(name, carrier)
        },
        selectedPlanAka (kind) {
            const value = this.planSelectValue(kind)
            if (!value) { return null }
            const hit = (this.plans[kind] || []).find(p => p.label === value)
            return hit ? hit.aka : null
        },
        // Which option the select should show. Options are keyed on the
        // display label; a survey saved before labels were stored holds the
        // raw catalog name, so match on either. Deliberately does NOT rewrite
        // the stored value -- normalising on load would dirty and re-save a
        // client's answer behind their back; it normalises when they next pick.
        planSelectValue (kind) {
            const stored = this.target[KIND[kind].planField]
            if (!stored) { return '' }
            const list = this.plans[kind] || []
            const hit = list.find(p => p.label === stored) || list.find(p => p.name === stored)
            return hit ? hit.label : stored
        },
        async loadPlans (kind) {
            this.$set(this.loadingPlans, kind, true)
            // Fan out to every catalog variant behind the merged display name
            // and union the plans. Deduped on the DISPLAY label, not the raw
            // name -- that is what collapses the discount variants. The catalog
            // embeds the carrier INCLUDING its marker in every plan name, so
            // the raw names differ per variant ("... (Standard) Medigap Core
            // Plan" vs "... (Standard 15% Disc) Medigap Core Plan") and
            // deduping on them showed one row per variant per plan: 12 options
            // for Harvard Pilgrim's 3 real Massachusetts plans. Once the
            // carrier is stripped both read "Medigap Core Plan" and merge.
            const base = this.baseCarrierName(this.target[KIND[kind].carrierField])
            const variants = this.variantsFor(kind, base)
            const resps = await Promise.all(variants.map(carrier => this.$api.searchPlans({
                ...this.scope,
                types: KIND[kind].types,
                carrier,
            }).catch(() => null)))
            const seen = new Set()
            const merged = []
            for (const resp of resps) {
                for (const plan of (resp?.results || [])) {
                    const label = this.planLabel(plan)
                    if (label && !seen.has(label)) {
                        seen.add(label)
                        merged.push({ ...plan, label, aka: this.planAka(plan, label) })
                    }
                }
            }
            this.$set(this.plans, kind, merged)
            this.$set(this.loadingPlans, kind, false)
        },
    },
}
